import React, { useState, useMemo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { Card, Row, Col, Input, Tabs, Table, Tag, Empty, Spin, Pagination, Divider } from 'antd'
import {
  SearchOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import useTasks from '../../../hooks/useTasks'
import SimpleHeader from '../../../helpers/cards-helper/SimpleHeader'
import { getStatusConfig, PRIORITY_COLORS, BRAND, BRAND_DIVIDER } from '../constants'

const { TabPane } = Tabs

const STATUS_ICONS = {
  pending: <ClockCircleOutlined />,
  processing: <SyncOutlined spin />,
  completed: <CheckCircleOutlined />,
  completed_partially: <CheckCircleOutlined />,
  refused: <CloseCircleOutlined />,
}

export default function TaskList() {
  const history = useHistory()
  const { tasks, loading, total, filters, pageSize, setPage, setStatus, setSearch } = useTasks()
  const [searchValue, setSearchValue] = useState('')

  const handleSearch = useCallback((value) => {
    setSearch(value)
  }, [setSearch])

  const handleTabChange = useCallback((key) => {
    setStatus(key === 'all' ? '' : key)
  }, [setStatus])

  const handleRowClick = useCallback((record) => {
    history.push(`/tasks/${record.id}`)
  }, [history])

  const columns = useMemo(() => [
    {
      title: '#',
      width: 50,
      align: 'center',
      render: (_, __, index) => <b>{(filters.page - 1) * pageSize + index + 1}</b>,
    },
    {
      title: 'Detyra',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text, record) => (
        <div>
          <span style={{ fontWeight: 500, color: BRAND }}>{text || record.task?.name}</span>
          {record.task?.description && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              {record.task.description.substring(0, 80)}
              {record.task.description.length > 80 ? '...' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Statusi',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      align: 'center',
      render: (status) => {
        const config = getStatusConfig(status)
        return (
          <Tag color={config.color} icon={STATUS_ICONS[status]}>
            {config.label}
          </Tag>
        )
      },
    },
    {
      title: 'Prioriteti',
      key: 'priority',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const priority = record.task?.task_priority
        if (!priority) return '-'
        const name = priority.name?.toLowerCase()
        return <Tag color={PRIORITY_COLORS[name] || 'default'}>{priority.name}</Tag>
      },
    },
    {
      title: 'Afati',
      key: 'due_date',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const date = record.end_date || record.due_date || record.task?.end_date
        if (!date) return '-'
        const m = moment(date)
        const isOverdue = m.isBefore(moment()) && record.status !== 'completed'
        return (
          <span style={{ color: isOverdue ? '#ff4d4f' : 'inherit' }}>
            {m.format('DD/MM/YYYY')}
          </span>
        )
      },
    },
    {
      title: 'Nga',
      key: 'creator',
      width: 150,
      render: (_, record) => {
        const creator = record.task?.create_member
        if (!creator) return '-'
        return `${creator.name || ''} ${creator.surname || ''}`.trim()
      },
    },
    {
      title: 'Pikat',
      key: 'points',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const pts = record.task?.points || record.points_taken
        return pts ? <Tag color="geekblue">{pts}</Tag> : '-'
      },
    },
  ], [filters.page, pageSize])

  return (
    <Row gutter={24} className="page-content">
      <Col span={24}>
        <SimpleHeader title="Detyrat e mia" subtitle="Detyrat" />
      </Col>

      <Col span={24} className="mt-2">
        <Card>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Kerko detyra..."
                prefix={<SearchOutlined />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onPressEnter={() => handleSearch(searchValue)}
                allowClear
                onClear={() => handleSearch('')}
                aria-label="Kerko detyra"
              />
            </Col>
          </Row>

          <Tabs
            defaultActiveKey="all"
            onChange={handleTabChange}
            style={{ marginTop: 12 }}
            tabBarStyle={{ color: BRAND }}
          >
            <TabPane tab={<span><UnorderedListOutlined /> Te gjitha</span>} key="all" />
            <TabPane tab={<span><ClockCircleOutlined /> Ne pritje</span>} key="pending" />
            <TabPane tab={<span><SyncOutlined /> Ne progres</span>} key="processing" />
            <TabPane tab={<span><CheckCircleOutlined /> Perfunduara</span>} key="completed" />
            <TabPane tab={<span><CloseCircleOutlined /> Refuzuara</span>} key="refused" />
          </Tabs>
        </Card>
      </Col>

      <Col span={24} className="mt-2">
        <Card bodyStyle={{ padding: '0px' }}>
          <Divider orientation="left" style={{ borderTopColor: BRAND_DIVIDER }} className="m-0">
            <h4 className="m-0" style={{ color: BRAND }}>Lista e detyrave</h4>
          </Divider>

          <Spin spinning={loading}>
            {tasks.length === 0 && !loading ? (
              <div style={{ padding: 40 }}>
                <Empty description="Nuk ka detyra" />
              </div>
            ) : (
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={tasks}
                  rowKey="id"
                  pagination={false}
                  onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                    style: { cursor: 'pointer' },
                  })}
                  size="small"
                  scroll={{ x: 800 }}
                />
              </div>
            )}
          </Spin>

          {total > pageSize && (
            <div style={{ textAlign: 'right', padding: '12px 16px' }}>
              <Pagination
                current={filters.page}
                total={total}
                pageSize={pageSize}
                onChange={setPage}
                showSizeChanger={false}
                showTotal={(t) => `${t} detyra gjithsej`}
              />
            </div>
          )}
        </Card>
      </Col>
    </Row>
  )
}
